import React, { useEffect, useMemo } from "react";
import Select from "react-dropdown-select";
import { IdentityFiscalInputsProps } from "@/screen/Identities/IdentityFiscalInputs/Interfaces/IdentityFiscalInputsInterfaces";
import type { CountryCatalogItem } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import { PendingFieldShell } from "@/screen/Identities/Styles/IdentityFormStyles";
import { FormSection, FormRow, FormField, FormLabel, FormInput, FormSelect, QuestionText, RadioGroup, RadioLabel, RadioInput, DisclaimerBox, SubQuestionIndent, SearchableSelectWrapper } from "@/screen/Identities/IdentityFiscalInputs/Styles/IdentityFiscalInputsStyles";
import { getKycServerErrorsByField, MaritalStatusMarriedValue, US_PERSON_DISCLAIMER_TEXT as DISCLAIMER_TEXT } from "@/screen/Identities/IdentityForm/identityKycRules";

const OTHER_NATIONALITIES_QUESTION_TEXT =
    "¿Tiene alguna otra nacionalidad además de la indicada en sus datos personales?";

const OPERATIONAL_BLOCK_TEXT = "Alta no permitida por operativa.";
const FATCA_ALLOWED_LABEL = "No sujeto a comunicación FATCA";
const FATCA_ALLOWED_LABEL_ALT = "No sujeto a comunicación FACTA";

function normalizeLabel(label: string | null | undefined): string
{
    if(!label)
    {
        return "";
    }
    return label
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
}

function getCatalogLabelByValue(items: {Value: number; Label: string}[] | null | undefined, value: number | null | undefined): string
{
    if(value === null || value === undefined || !items || items.length === 0)
    {
        return "";
    }
    const selected = items.find((item) => item.Value === value);
    return normalizeLabel(selected?.Label);
}

function selectNumericValue(v: number | null | undefined): string
{
    if(v === null || v === undefined)
    {
        return "";
    }
    return String(v);
}

const IdentityFiscalInputs: React.FC<IdentityFiscalInputsProps> = ({formData, validation, highlightPendingManualFields, isFormLocked, onChange, onFiscalBlocked, identityCatalogs, catalogsLoading, catalogsError, countriesCatalog, serverErrors}) =>
{
    const ph = (ok: boolean): boolean => highlightPendingManualFields && !ok;
    // Errores KYC_* del servidor anclados a la nacionalidad (p. ej. KYC_BLOCKED_NATIONALITY).
    const kycServerErrorsByField = useMemo(() => getKycServerErrorsByField(serverErrors), [serverErrors]);
    const nationalityServerErrors = [...(kycServerErrorsByField.otherNationalities ?? []), ...(kycServerErrorsByField.otherNationality ?? [])];
    const selectedFatcaStatusLabel = getCatalogLabelByValue(identityCatalogs?.FatcaStatuses, formData.fatcaStatus);
    const normalizedAllowedFatcaLabel = normalizeLabel(FATCA_ALLOWED_LABEL);
    const normalizedAllowedFatcaLabelAlt = normalizeLabel(FATCA_ALLOWED_LABEL_ALT);
    const hasOperationalFatcaBlock = selectedFatcaStatusLabel !== "" && selectedFatcaStatusLabel !== normalizedAllowedFatcaLabel && selectedFatcaStatusLabel !== normalizedAllowedFatcaLabelAlt;
    const isFiscalBlocked = useMemo(() =>
    {
        const isUsResidence = formData.residenceCountry === "US";
        const hasTaxIdInUS = formData.hasTaxIdInUS === true;
        const hasResidenceInUS = formData.hasResidenceInUS === true;
        const hasTaxObligationInUS = formData.hasTaxObligationInUS === true;
        return isUsResidence || hasTaxIdInUS || hasResidenceInUS || hasTaxObligationInUS || hasOperationalFatcaBlock;
    }, [formData.residenceCountry, formData.hasTaxIdInUS, formData.hasResidenceInUS, formData.hasTaxObligationInUS, hasOperationalFatcaBlock]);

    useEffect(() =>
    {
        onFiscalBlocked(isFiscalBlocked);
    }, [isFiscalBlocked, onFiscalBlocked]);

    const maritalOptions = identityCatalogs?.MaritalStatuses ?? [];
    const maritalSituationOptions = identityCatalogs?.MaritalSituations ?? [];
    const fatcaOptions = identityCatalogs?.FatcaStatuses ?? [];
    const residenceOfficialOptions = identityCatalogs?.ResidenceOfficialDocuments ?? [];
    const noResidenceOptions = identityCatalogs?.NoResidenceDocuments ?? [];

    // El país de residencia se pinta con buscador (igual que Ciudad y Código Postal del bloque
    // de dirección), por el tamaño del catálogo. El controller sigue recibiendo un evento de
    // select para no duplicar la lógica de handleChange (limpieza de documentos según país).
    const selectedResidenceCountryOption = useMemo<CountryCatalogItem[]>(() =>
    {
        const residenceCountry = formData.residenceCountry || "";
        if(residenceCountry === "")
        {
            return [];
        }
        const match = countriesCatalog.find((country) => country.value === residenceCountry);
        return match ? [match] : [];
    }, [formData.residenceCountry, countriesCatalog]);

    const handleResidenceCountryChange = (value: string): void =>
    {
        onChange({target: {name: "residenceCountry", value}} as React.ChangeEvent<HTMLSelectElement>);
    };

    const selectDisabled = isFormLocked || catalogsLoading;
    const isSpainResidence = formData.residenceCountry === "ES";
    const hasResidenceCountry = !!formData.residenceCountry && formData.residenceCountry !== "";
    // El régimen económico matrimonial (campo KYC) solo aplica cuando el estado civil es Casado.
    const isMarried = formData.maritalStatus === MaritalStatusMarriedValue;

    return(
        <FormSection>
            <h3>Datos Fiscales</h3>
            {catalogsError &&
            (
                <p style={{color: "#a4262c", fontSize: "13px", marginBottom: "12px"}} role="alert">
                    {catalogsError}
                </p>
            )}
            <FormRow>
                <FormField>
                    <FormLabel required>Estado Civil</FormLabel>
                    <FormSelect name="maritalStatus" value={selectNumericValue(formData.maritalStatus)} onChange={onChange} disabled={selectDisabled} $pendingHighlight={ph(validation.maritalStatus)}>
                        <option value="">{catalogsLoading ? "Cargando…" : "-- Seleccionar --"}</option>
                        {maritalOptions.map((opt) =>
                        (
                            <option key={opt.Value} value={String(opt.Value)}>{opt.Label}</option>
                        ))}
                    </FormSelect>
                </FormField>
                {/* El selector de Profesión (camino viejo) desaparece; en su lugar,
                    el Régimen Económico Matrimonial, visible solo cuando el estado civil es Casado. */}
                {isMarried ?
                (
                    <FormField>
                        <FormLabel required>Régimen Económico Matrimonial</FormLabel>
                        <FormSelect name="maritalSituation" value={formData.maritalSituation ?? ""} onChange={onChange} disabled={selectDisabled} $pendingHighlight={ph(validation.maritalSituation)}>
                            <option value="">{catalogsLoading ? "Cargando…" : "-- Seleccionar --"}</option>
                            {maritalSituationOptions.map((opt) =>
                            (
                                <option key={opt.code} value={opt.code}>{opt.label}</option>
                            ))}
                        </FormSelect>
                    </FormField>
                ) :
                (
                    <FormField />
                )}
            </FormRow>
            <FormRow>
                <FormField>
                    <FormLabel required>País de Residencia</FormLabel>
                    <SearchableSelectWrapper $pendingHighlight={ph(validation.residenceCountry)} $disabled={isFormLocked || countriesCatalog.length === 0}>
                        <Select<CountryCatalogItem>
                            options={countriesCatalog}
                            values={selectedResidenceCountryOption}
                            onChange={(selected) => handleResidenceCountryChange(selected[0]?.value ?? "")}
                            placeholder="-- Seleccionar --"
                            searchable
                            clearable={false}
                            dropdownPosition="bottom"
                            dropdownHeight="260px"
                            labelField="text"
                            valueField="value"
                            searchBy="text"
                            disabled={isFormLocked || countriesCatalog.length === 0}
                            name="residenceCountry"
                            noDataLabel="No hay resultados"
                            className="identity-residence-country-select"
                        />
                    </SearchableSelectWrapper>
                </FormField>
                <FormField>
                    <FormLabel required>FATCA</FormLabel>
                    <FormSelect name="fatcaStatus" value={selectNumericValue(formData.fatcaStatus)} onChange={onChange} disabled={selectDisabled}
                        $pendingHighlight={ph(validation.fatcaStatus)}
                    >
                        <option value="">{catalogsLoading ? "Cargando…" : "-- Seleccionar --"}</option>
                        {fatcaOptions.map((opt) =>
                        (
                            <option key={opt.Value} value={String(opt.Value)}>{opt.Label}</option>
                        ))}
                    </FormSelect>
                </FormField>
            </FormRow>
            {hasResidenceCountry &&
            (
                <FormRow>
                    {isSpainResidence &&
                    (
                        <FormField>
                            <FormLabel required>Documento oficial de residencia</FormLabel>
                            <FormSelect
                                name="residenceOfficialDocument"
                                value={selectNumericValue(formData.residenceOfficialDocument)}
                                onChange={onChange}
                                disabled={selectDisabled}
                                $pendingHighlight={ph(validation.residenceOfficialDocument)}
                            >
                                <option value="">{catalogsLoading ? "Cargando…" : "-- Seleccionar --"}</option>
                                {residenceOfficialOptions.map((opt) =>
                                (
                                    <option key={opt.Value} value={String(opt.Value)}>{opt.Label}</option>
                                ))}
                            </FormSelect>
                        </FormField>
                    )}
                    {!isSpainResidence &&
                    (
                        <FormField>
                            <FormLabel required>Documento (no residente)</FormLabel>
                            <FormSelect
                                name="noResidenceDocument"
                                value={selectNumericValue(formData.noResidenceDocument)}
                                onChange={onChange}
                                disabled={selectDisabled}
                                $pendingHighlight={ph(validation.noResidenceDocument)}
                            >
                                <option value="">{catalogsLoading ? "Cargando…" : "-- Seleccionar --"}</option>
                                {noResidenceOptions.map((opt) =>
                                (
                                    <option key={opt.Value} value={String(opt.Value)}>{opt.Label}</option>
                                ))}
                            </FormSelect>
                        </FormField>
                    )}
                </FormRow>
            )}
            <FormRow>
                <FormField style={{flex: "1"}}>
                    <QuestionText>¿Tiene número de identificación fiscal en EEUU?</QuestionText>
                    <PendingFieldShell $pendingHighlight={ph(validation.hasTaxIdInUS)}>
                        <RadioGroup>
                            <RadioLabel>
                                <RadioInput type="radio" name="hasTaxIdInUS" value="true" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.hasTaxIdInUS === true}
                                />
                                Sí
                            </RadioLabel>
                            <RadioLabel>
                                <RadioInput type="radio" name="hasTaxIdInUS" value="false" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.hasTaxIdInUS === false}
                                />
                                No
                            </RadioLabel>
                        </RadioGroup>
                    </PendingFieldShell>
                </FormField>
                <FormField style={{flex: "1"}}>
                    <QuestionText>¿Tiene domicilio o residencia en EEUU?</QuestionText>
                    <PendingFieldShell $pendingHighlight={ph(validation.hasResidenceInUS)}>
                        <RadioGroup>
                            <RadioLabel>
                                <RadioInput type="radio" name="hasResidenceInUS" value="true" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.hasResidenceInUS === true}
                                />
                                Sí
                            </RadioLabel>
                            <RadioLabel>
                                <RadioInput type="radio" name="hasResidenceInUS" value="false" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.hasResidenceInUS === false}
                                />
                                No
                            </RadioLabel>
                        </RadioGroup>
                    </PendingFieldShell>
                </FormField>
            </FormRow>
            <FormRow>
                <FormField style={{flex: "1"}}>
                    <QuestionText>¿Tiene obligación de pago de impuestos en EEUU?</QuestionText>
                    <PendingFieldShell $pendingHighlight={ph(validation.hasTaxObligationInUS)}>
                        <RadioGroup>
                            <RadioLabel>
                                <RadioInput type="radio" name="hasTaxObligationInUS" value="true" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.hasTaxObligationInUS === true}
                                />
                                Sí
                            </RadioLabel>
                            <RadioLabel>
                                <RadioInput type="radio" name="hasTaxObligationInUS" value="false" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.hasTaxObligationInUS === false}
                                />
                                No
                            </RadioLabel>
                        </RadioGroup>
                    </PendingFieldShell>
                </FormField>
                {/* La otra nacionalidad ocupa el hueco junto a las preguntas de EEUU.
                    Para menores esta sección no se pinta: la pregunta se muestra entonces en el bloque KYC. */}
                <FormField style={{flex: "1"}}>
                    <QuestionText>{OTHER_NATIONALITIES_QUESTION_TEXT}</QuestionText>
                    <PendingFieldShell $pendingHighlight={ph(validation.otherNationalities)}>
                        <RadioGroup>
                            <RadioLabel>
                                <RadioInput type="radio" name="otherNationalities" value="true" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.otherNationalities === true}
                                />
                                Sí
                            </RadioLabel>
                            <RadioLabel>
                                <RadioInput type="radio" name="otherNationalities" value="false" disabled={isFormLocked}
                                    onChange={onChange} checked={formData.otherNationalities === false}
                                />
                                No
                            </RadioLabel>
                        </RadioGroup>
                    </PendingFieldShell>
                    {formData.otherNationalities === true &&
                    (
                        <SubQuestionIndent>
                            <FormLabel required>Indique su otra nacionalidad</FormLabel>
                            <FormInput type="text" name="otherNationality" maxLength={50} disabled={isFormLocked} autoComplete="off"
                                value={formData.otherNationality} onChange={onChange}
                                $pendingHighlight={ph(validation.otherNationality)}
                            />
                        </SubQuestionIndent>
                    )}
                    {nationalityServerErrors.map((message, index) =>
                    (
                        <p key={index} style={{color: "#a4262c", fontSize: "12px", marginTop: "6px"}} role="alert">
                            {message}
                        </p>
                    ))}
                </FormField>
            </FormRow>
            {isFiscalBlocked &&
            (
                <DisclaimerBox>
                    <p>{hasOperationalFatcaBlock ? OPERATIONAL_BLOCK_TEXT : DISCLAIMER_TEXT}</p>
                </DisclaimerBox>
            )}
        </FormSection>
    );
};

export default IdentityFiscalInputs;